import React, { useState, useRef, useEffect } from 'react';
import './MobileSideNav.scss';
import { FlattenedMenuItem } from '../../../utils/menuFlattener';
import { shouldShowMenuItem } from '../utils/menuVisibility';
import ProfileButton from '../ProfileButton/ProfileButton';
import closeIcon from '../../../images/icons/close.png';
import { HeaderMode } from '../../../contexts/MinisiteLayoutContext';
import { buildLoginUrl } from '../../../utils/authHelper';
import { useLocation } from 'react-router-dom';

interface MobileSideNavProps {
  mode?: HeaderMode;
  isActive: boolean;
  onClose: () => void;
  navItems?: FlattenedMenuItem[];
  secondaryNavItems?: FlattenedMenuItem[];
  watchMenuItems?: FlattenedMenuItem[];
  cust_type?: string;
  username?: string;
  currentLang?: 'en' | 'zh';
  onLoginClick?: () => void;
  loginUrl?: string;
  onLanguageChange?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
  };
  isProfileLoading?: boolean;
}

const SubMenuToggle: React.FC<{
  title: string;
  children: React.ReactNode;
  isActive?: boolean;
}> = ({ title, children, isActive = false }) => {
  const [isOpen, setIsOpen] = useState(isActive);
  const subMenuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (subMenuRef.current) {
      const subMenu = subMenuRef.current;
      if (isOpen) {
        const contentHeight = subMenu.scrollHeight;
        subMenu.style.height = `${contentHeight}px`;
      } else {
        subMenu.style.height = '0px';
      }
    }
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <li className={`hasSubMenu ${isOpen ? 'active' : ''}`}>
      <a href="#" className='openSubMenu-btn' onClick={handleToggle}>
        {title}
      </a>
      <ul ref={subMenuRef} className={`subMenu ${isOpen ? 'active' : ''}`}>
        {children}
      </ul>
    </li>
  );
};

const MobileSideNav: React.FC<MobileSideNavProps> = ({ 
  mode,
  isActive,
  onClose,
  navItems = [],
  secondaryNavItems = [],
  watchMenuItems = [],
  cust_type = 'not-logged-in',
  username,
  currentLang = 'en',
  onLoginClick,
  loginUrl = '#',
  onLanguageChange,
  socialLinks,
  isProfileLoading = false
}) => {
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/$/, '') || '/';

  const langText = currentLang === 'zh' ? 'ENG' : '中文';
  const langValue = currentLang === 'zh' ? "op/UwDjU7jkgMlWyDIDgUg==" : "mcHbFvVz6GasaoYXRD6O4Q==";

  // Helper to check if a link is active
  const isActiveLink = (path?: string) => {
    if (!path || path === '#' || path.startsWith('javascript:')) return false;
    
    try {
      // Check if it's an absolute URL
      const isAbsolute = path.startsWith('http');
      const url = isAbsolute ? new URL(path) : new URL(path, window.location.origin);
      
      // If absolute, it must match current origin to be active
      if (isAbsolute && url.origin !== window.location.origin) {
        return false;
      }

      const normalizedPath = url.pathname.replace(/\/$/, '') || '/';
      return currentPath === normalizedPath;
    } catch (e) {
      // Fallback for non-standard paths
      const normalizedPath = path.replace(/\/$/, '') || '/';
      return currentPath === normalizedPath;
    }
  };

  // Filter menu items based on customer type
  const filteredNavItems = navItems.filter(item => 
    shouldShowMenuItem(item.displayFor, cust_type)
  );

  return (
    <nav className={`sideMenu mobile-side-nav ${isActive ? 'active' : ''}`}>
      <div className="head">
        <div className="controls">
          <button className="lang-button" onClick={onLanguageChange} data-value={langValue}>
            {langText}
          </button>
          <button className="nav-close-button" onClick={onClose} aria-label="Close menu">
            <img src={closeIcon} alt="Close" />
          </button>
        </div>
        
        <div className="button-wrapper">
            <ProfileButton 
              status={username ? 'logged-in' : 'login'}
              username={username}
              onClick={onLoginClick}
              href={username ? '/my-account' : loginUrl}
              currentLang={currentLang}
              isLoading={isProfileLoading}
            />
          
          {/* Watch menu section */}
          <div className="watch-menu-wrapper">
            <div className="menuList">
              <ul>
                {watchMenuItems.length > 0 ? (
                  watchMenuItems
                    .filter(item => shouldShowMenuItem(item.displayFor, cust_type))
                    .map((item, idx) => {
                      const hasChildren = item.children && item.children.length > 0;
                      const filteredChildren = item.children?.filter(child => 
                        shouldShowMenuItem(child.displayFor, cust_type)
                      );
                      
                      const isItemActive = isActiveLink(item.url);
                      const isChildActive = filteredChildren?.some(child => isActiveLink(child.url)) ?? false;

                      if (hasChildren && filteredChildren && filteredChildren.length > 0) {
                        return (
                          <SubMenuToggle key={idx} title={item.label} isActive={isItemActive || isChildActive}>
                            {filteredChildren.map((child, cidx) => (
                              <li key={cidx}>
                                <a href={(child.forceLogin && !username) ? buildLoginUrl(currentLang as 'en' | 'zh', child.url || '#') : (child.url || '#')} target={child.target || ''} rel={child.target === '_blank' ? "noopener noreferrer" : ""}>
                                  <span>{child.label}</span>
                                </a>
                              </li>
                            ))}
                          </SubMenuToggle>
                        );
                      }
                      
                      return (
                        <li key={idx} className={isItemActive ? 'active' : ''}>
                          <a href={(item.forceLogin && !username) ? buildLoginUrl(currentLang as 'en' | 'zh', item.url || '#') : (item.url || '#')} target={item.target || ''} rel={item.target === '_blank' ? "noopener noreferrer" : ""}>
                            {item.label}
                          </a>
                        </li>
                      );
                    })
                ) : (
                  <SubMenuToggle title={currentLang === 'zh' ? '立即觀看' : 'Watch'}>
                    <li>
                      <a href={`https://nowplayer.now.com/?lang=${currentLang === 'zh' ? 'zh' : 'en'}`} target="_blank" rel="noopener noreferrer">
                        <span>{currentLang === 'zh' ? 'Now TV 主頁' : 'Now TV Home'}</span>
                      </a>
                    </li>
                    <li>
                      <a href={`https://nowplayer.now.com/tvguide?lang=${currentLang === 'zh' ? 'zh' : 'en'}`} target="_blank" rel="noopener noreferrer">
                        <span>{currentLang === 'zh' ? '電視指南' : 'TV Guide'}</span>
                      </a>
                    </li>
                    <li>
                      <a href={`https://nowplayer.now.com/ondemand?lang=${currentLang === 'zh' ? 'zh' : 'en'}`} target="_blank" rel="noopener noreferrer">
                        <span>{currentLang === 'zh' ? '隨選' : 'On Demand'}</span>
                      </a>
                    </li>
                  </SubMenuToggle>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {mode !== 'logo-lang-enquiry' && (
        <div className="mobile-menu-wrapper">
          <div className="menu-wrapper">
            <div className="menuList">
              <ul>
                <li className={isActiveLink('/') ? 'active' : ''}>
                  <a href="/">
                    {currentLang === 'zh' ? '首頁' : 'Home'}
                  </a>
                </li>
                {filteredNavItems.map((item, idx) => {
                  const hasChildren = item.children && item.children.length > 0;
                  const filteredChildren = item.children?.filter(child => 
                    shouldShowMenuItem(child.displayFor, cust_type)
                  );
                  
                  const hasVisibleChildren = hasChildren && filteredChildren && filteredChildren.length > 0;

                  if (hasVisibleChildren) {
                    return (
                      <SubMenuToggle key={idx} title={item.label} isActive={false}>
                        {filteredChildren.map((child, cidx) => (
                          <li key={cidx}>
                            <a href={(child.forceLogin && !username) ? buildLoginUrl(currentLang as 'en' | 'zh', child.url || '#') : (child.url || '#')} target={child.target || ''}>
                              <span>{child.label}</span>
                            </a>
                          </li>
                        ))}
                      </SubMenuToggle>
                    );
                  }
                  
                  return (
                    <li key={idx} className={isActiveLink(item.url) ? 'active' : ''}>
                      <a href={(item.forceLogin && !username) ? buildLoginUrl(currentLang as 'en' | 'zh', item.url || '#') : (item.url || '#')} target={item.target || ''}>
                        {item.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          
          <div className='divider'><hr /></div>
          
          {/* Secondary/Footer Menu */}
          {secondaryNavItems.length > 0 && (
            <>
              <div className="menu-wrapper footer-menu-wrapper">
                <div className="menuList">
                  <ul>
                    {secondaryNavItems.filter(item => 
                      shouldShowMenuItem(item.displayFor, cust_type)
                    ).map((item, idx) => {
                      const hasChildren = item.children && item.children.length > 0;
                      const filteredChildren = item.children?.filter(child => 
                        shouldShowMenuItem(child.displayFor, cust_type)
                      );
                      
                      const hasVisibleChildren = hasChildren && filteredChildren && filteredChildren.length > 0;

                      if (hasVisibleChildren) {
                        return (
                          <SubMenuToggle key={idx} title={item.label} isActive={false}>
                            {filteredChildren.map((child, cidx) => (
                              <li key={cidx}>
                                <a href={(child.forceLogin && !username) ? buildLoginUrl(currentLang as 'en' | 'zh', child.url || '#') : (child.url || '#')} target={child.target || ''}>
                                  <span>{child.label}</span>
                                </a>
                              </li>
                            ))}
                          </SubMenuToggle>
                        );
                      }
                      
                      return (
                        <li key={idx} className={isActiveLink(item.url) ? 'active' : ''}>
                          <a href={(item.forceLogin && !username) ? buildLoginUrl(currentLang as 'en' | 'zh', item.url || '#') : (item.url || '#')} target={item.target || ''}>
                            {item.label}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
              <div className='divider'><hr /></div>
            </>
          )}
        </div>
      )}

      {/* Social Media Links */}
      <div className='social-media-wrapper'>
        {socialLinks?.facebook && (
          <div className='item'>
            <a href={socialLinks.facebook} className="fb" target="_blank" rel="noopener noreferrer" aria-label="Facebook"></a>
          </div>
        )}
        {socialLinks?.instagram && (
          <div className='item'>
            <a href={socialLinks.instagram} className="ins" target="_blank" rel="noopener noreferrer" aria-label="Instagram"></a>
          </div>
        )}
        {socialLinks?.youtube && (
          <div className='item'>
            <a href={socialLinks.youtube} className="yt" target="_blank" rel="noopener noreferrer" aria-label="YouTube"></a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default MobileSideNav;
